import APIClient from './index';
import _ from 'lodash';

export const getEmailTemplates = () => {
  return new Promise((resolve, reject) => {
    APIClient.get('Admin/email-templates')
      .then(response => resolve(response.data))
      .catch(err => reject(_.get(err, 'data.error')));
  });
};

export const renderEmailTemplate = (templateName, data = {}) => {
  return new Promise((resolve, reject) => {
    APIClient.post(`Admin/email-templates/${templateName}/render`, data)
      .then(response => resolve(response.data))
      .catch(err => reject(_.get(err, 'data.error')));
  });
};

export const sendTestEmail = (templateName, { subject, html }) => {
  return new Promise((resolve, reject) => {
    APIClient.post(`Admin/email-templates/${templateName}/send`, { subject, html })
      .then(response => resolve(response.data))
      .catch(err => reject(_.get(err, 'data.error')));
  });
};

export const sendTestPush = (templateName, body) => {
  return new Promise((resolve, reject) => {
    APIClient.post(`Admin/email-templates/${templateName}/send-push`, body)
      .then(response => resolve(response.data))
      .catch(err => reject(_.get(err, 'data.error')));
  });
};

export const fireTestEvent = (templateName, body) => {
  return new Promise((resolve, reject) => {
    APIClient.post(`Admin/email-templates/${templateName}/fire-event`, body)
      .then(response => resolve(response.data))
      .catch(err => reject(_.get(err, 'data.error')));
  });
};

export const getNotificationSends = (range = '7d') => {
  return new Promise((resolve, reject) => {
    APIClient.get(`Admin/notifications/sends?range=${encodeURIComponent(range)}`)
      .then(response => resolve(response.data))
      .catch(err => reject(_.get(err, 'data.error')));
  });
};

export const searchNotificationRecipients = (q = '') => {
  return new Promise((resolve, reject) => {
    APIClient.get(`AdminNotificationRecipients/search?q=${encodeURIComponent(q)}`)
      .then(response => resolve(response.data))
      .catch(err => reject(_.get(err, 'data.error')));
  });
};

export const getNotificationRecipientTemplates = () => {
  return new Promise((resolve, reject) => {
    APIClient.get('AdminNotificationRecipients/templates')
      .then(response => resolve(response.data))
      .catch(err => reject(_.get(err, 'data.error')));
  });
};

const recipientParams = (range, filters = {}, cursor = null) => new URLSearchParams(_.omitBy({ range, ...filters, cursor }, v => !v));

export const getNotificationRecipient = (userId, range = '7d', filters = {}) => {
  return new Promise((resolve, reject) => {
    APIClient.get(`AdminNotificationRecipients/${userId}?${recipientParams(range, filters).toString()}`)
      .then(response => resolve(response.data))
      .catch(err => reject(_.get(err, 'data.error')));
  });
};

export const getNotificationRecipientReceipts = (userId, range = '7d', filters = {}, cursor = null) => {
  return new Promise((resolve, reject) => {
    const params = recipientParams(range, filters, cursor);
    APIClient.get(`AdminNotificationRecipients/${userId}/receipts?${params.toString()}`)
      .then(response => resolve(response.data))
      .catch(err => reject(_.get(err, 'data.error')));
  });
};

export const checkNotificationForRecipient = (userId, templateName) => {
  return new Promise((resolve, reject) => {
    APIClient.post(`AdminNotificationRecipients/${userId}/check`, { templateName })
      .then(response => resolve(response.data))
      .catch(err => reject(_.get(err, 'data.error')));
  });
};

export const searchBrandsForPreview = (query = '') => {
  return new Promise((resolve, reject) => {
    APIClient.get(`Admin/email-templates/search-brands?query=${encodeURIComponent(query)}`)
      .then(response => resolve(response.data))
      .catch(err => reject(_.get(err, 'data.error')));
  });
};

const registryRequest = request => {
  return new Promise((resolve, reject) => {
    request.then(response => resolve(response.data)).catch(err => reject(_.get(err, 'data') || { error: 'Request failed.' }));
  });
};

export const getNotificationRegistry = () => {
  return registryRequest(APIClient.get('AdminNotificationRegistry'));
};

export const getNotificationLaunches = () => {
  return registryRequest(APIClient.get('AdminNotificationRegistry/launches'));
};

export const getNotificationRegistryOptions = () => {
  return registryRequest(APIClient.get('AdminNotificationRegistry/options'));
};

export const getNotificationRegistryEntry = type => {
  return registryRequest(APIClient.get(`AdminNotificationRegistry/${encodeURIComponent(type)}`));
};

export const createNotificationRegistryEntry = body => {
  return registryRequest(APIClient.post('AdminNotificationRegistry', body));
};

export const updateNotificationRegistryEntry = (type, fields) => {
  return registryRequest(APIClient.put(`AdminNotificationRegistry/${encodeURIComponent(type)}`, { fields }));
};

export const deleteNotificationRegistryEntry = type => {
  return registryRequest(APIClient.delete(`AdminNotificationRegistry/${encodeURIComponent(type)}`));
};

export const retireNotificationRegistryEntry = (type, reason) => {
  return registryRequest(APIClient.post(`AdminNotificationRegistry/${encodeURIComponent(type)}/retire`, { reason }));
};

export const restoreNotificationRegistryEntry = type => {
  return registryRequest(APIClient.post(`AdminNotificationRegistry/${encodeURIComponent(type)}/restore`));
};

// Draft with AI for a plan that is not saved yet: { plan, inputs, surfaces, want: 'copy' | 'inputs' } → { copy | inputs, issues }.
export const draftForNewNotification = body => {
  return registryRequest(APIClient.post('AdminNotificationRegistry/draft', body));
};

// Draft with AI on a saved notification: drafts or inputs are written for it and the page comes back.
export const draftForNotification = (type, body) => {
  return registryRequest(APIClient.post(`AdminNotificationRegistry/${encodeURIComponent(type)}/draft`, body));
};

export const saveNotificationCopyDraft = (type, surface, content, extra = {}) => {
  return registryRequest(APIClient.put(`AdminNotificationRegistry/${encodeURIComponent(type)}/copy/${surface}`, { content, ...extra }));
};

// Deletes the working draft; the server keeps its words in History.
export const withdrawNotificationCopyDraft = (type, surface, extra = {}) => saveNotificationCopyDraft(type, surface, null, extra);

export const approveNotificationCopy = (type, surface, extra = {}) => {
  return registryRequest(
    APIClient.post(`AdminNotificationRegistry/${encodeURIComponent(type)}/copy/${surface}/approve`, { confirm: true, ...extra })
  );
};

export const withdrawNotificationCopy = (type, surface, extra = {}) => {
  return registryRequest(APIClient.post(`AdminNotificationRegistry/${encodeURIComponent(type)}/copy/${surface}/withdraw`, extra));
};

export const revokeNotificationCopyApproval = (type, surface) => {
  return registryRequest(APIClient.post(`AdminNotificationRegistry/${encodeURIComponent(type)}/copy/${surface}/revoke`, { confirm: true }));
};

export const setNotificationCopyLiveInFlow = (type, surface, liveInFlow, extra = {}) => {
  return registryRequest(
    APIClient.post(`AdminNotificationRegistry/${encodeURIComponent(type)}/copy/${surface}/live-in-flow`, { confirm: true, liveInFlow, ...extra })
  );
};

// Who else could share this notification's Notification Center row: { compatible, nearMisses }.
export const getNotificationRowGroupCandidates = type => {
  return registryRequest(APIClient.get(`AdminNotificationRegistry/${encodeURIComponent(type)}/row-group/candidates`));
};

export const createNotificationRowGroup = (type, body) => {
  return registryRequest(APIClient.post(`AdminNotificationRegistry/${encodeURIComponent(type)}/row-group`, body));
};

// Members, the type the words live on and the name, any combination.
export const updateNotificationRowGroup = (type, body) => {
  return registryRequest(APIClient.put(`AdminNotificationRegistry/${encodeURIComponent(type)}/row-group`, body));
};

export const leaveNotificationRowGroup = (type, body = {}) => {
  return registryRequest(APIClient.delete(`AdminNotificationRegistry/${encodeURIComponent(type)}/row-group`, { data: body }));
};

export const acknowledgeNotificationPlanDifference = type => {
  return registryRequest(APIClient.post(`AdminNotificationRegistry/${encodeURIComponent(type)}/acknowledge-difference`, {}));
};

export const updateNotificationRollout = (type, body) => {
  return registryRequest(APIClient.put(`AdminNotificationRegistry/${encodeURIComponent(type)}/rollout`, body));
};

// shadowPeriodDays and holdoutPercent, either or both; null goes back to the budget category's default.
export const updateNotificationLaunchPlan = (type, body) => {
  return registryRequest(APIClient.put(`AdminNotificationRegistry/${encodeURIComponent(type)}/launchPlan`, body));
};

// The starter file's text; the caller hands it to downloadTextFile, since a bare link would carry no auth headers.
export const getNotificationTemplateStarter = type => {
  return registryRequest(
    APIClient.get(`AdminNotificationRegistry/${encodeURIComponent(type)}/starter`, { responseType: 'text', transformResponse: [text => text] })
  );
};
